import {
  Box,
  Button,
  CloseButton,
  createStyles,
  Group,
  Space,
  Stack,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import React, { useState } from 'react';

import {
  BarControllerWidget,
  BarControllerWidgetForm,
  BarControllerWidgetFormType,
  convertWidgetFormToWidgetProps,
} from '@portals/device-widgets';
import { useConfirmationModal } from '@portals/framework';

import { CommonWidgetFormWrapperProps } from './form-wrappers.types';
import { useNumericCommandOptions } from '../device-widgets.hooks';

export function BarControllerWidgetFormWrapper({
  initialValues,
  onSubmit,
  onCancel,
  onClose,
  modelId,
  colors,
  onAddCustomColor,
}: CommonWidgetFormWrapperProps<BarControllerWidgetFormType>) {
  const { classes } = useStyles();
  const [value, setValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const asyncConfirmationCheck = useConfirmationModal();

  const form = useForm<BarControllerWidgetFormType>({
    initialValues: {
      name: '',
      telemetry: '',
      unit: '',
      telemetry_as_title: false,
      title_telemetry: '',
      gradient: {
        from: 'blue_accent.4',
        to: 'deep_purple_accent.2',
      },
      command_name: '',
      command_param_key: '',
      min: 0,
      max: 100,
      ...(initialValues || {}),
    },
    validateInputOnBlur: true,
    validate: {
      min: (value, values) => {
        if (isNaN(value)) return 'Min value must be a number';

        return value >= values.max ? 'Min value must be less than max' : null;
      },
      max: (value, values) => {
        if (isNaN(value)) return 'Max value must be a number';

        return value <= values.min
          ? 'Max value must be greater than min'
          : null;
      },
    },
  });

  const { commandsOptions, paramsOptions } =
    useNumericCommandOptions<BarControllerWidgetFormType>({
      modelId,
      form,
      commandFieldName: 'command_name',
      commandParamFieldName: 'command_param_key',
    });

  const onChange = (value: number) => {
    setValue(value);
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const onCloseWrapper = async () => {
    if (!form.isDirty()) {
      onClose();
      return;
    }

    const isConfirmed = await asyncConfirmationCheck({
      title: 'Are you sure?',
      description: 'All your changes will be lost',
      confirmationLabel: 'Confirm',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) onClose();
  };

  const onCancelWrapper = async () => {
    if (!form.isDirty()) {
      onCancel();
      return;
    }

    const isConfirmed = await asyncConfirmationCheck({
      title: 'Are you sure?',
      description: 'All your changes will be lost',
      confirmationLabel: 'Confirm',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) onCancel();
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Box className={classes.container}>
        <Stack className={classes.preview} p={30} pb={0} spacing={0}>
          <Text size={24} color="blue_gray.9">
            Add Bar Controller
          </Text>

          <Space h="xl" />

          <BarControllerWidgetForm
            form={form}
            commands={commandsOptions}
            params={paramsOptions}
            colors={colors}
            onAddCustomColor={onAddCustomColor}
          />

          <Space h="xl" />
        </Stack>

        <Stack className={classes.config} p={30}>
          <Group position="right">
            <CloseButton size="xl" onClick={onCloseWrapper} />
          </Group>

          <Stack justify="center" spacing="xl" h="100%">
            <BarControllerWidget
              {...convertWidgetFormToWidgetProps.bar_controller({
                formValues: form.values,
                value,
                onChange,
                isLoading,
              })}
              stackProps={{ h: 167, w: '100%' }}
            />
          </Stack>
        </Stack>

        <Group className={classes.footer} p="xl" position="right">
          <Button variant="default" onClick={onCancelWrapper}>
            {initialValues ? 'Close' : 'Back'}
          </Button>

          <Button
            type="submit"
            data-testid={initialValues ? 'save-widget-btn' : 'add-widget-btn'}
          >
            {initialValues ? 'Save' : 'Add Widget'}
          </Button>
        </Group>
      </Box>
    </form>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr min-content',
    gridTemplateAreas: `
      "preview config"
      "footer footer"
    `,
    gap: 0,
    height: 790,
  },
  preview: {
    gridArea: 'preview',
  },
  config: {
    gridArea: 'config',
    backgroundColor: theme.colors.gray[0],
  },
  footer: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    gridArea: 'footer',
  },
}));
